import { Link } from "react-router";
import { PublicPageLayout } from "@/components/PublicPageLayout";

const TEXT_LINK =
  "font-medium text-[var(--cs-navy)] underline decoration-[#AFC2D0] underline-offset-4 transition-colors hover:decoration-[var(--cs-navy)]";

function AboutSection({
  number,
  title,
  children,
}: {
  number: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="border-t border-[#DCE4EA] py-9 sm:py-11">
      <div className="grid gap-5 md:grid-cols-[120px_minmax(0,1fr)] md:gap-10">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#738093]">
            {number}
          </p>
          <h2 className="mt-2 text-[24px] font-semibold leading-tight tracking-[-0.02em] text-[var(--cs-navy)]">
            {title}
          </h2>
        </div>
        <div className="space-y-5 text-[15px] leading-7 text-[#536274]">{children}</div>
      </div>
    </section>
  );
}

export function AboutPage() {
  return (
    <PublicPageLayout>
      <article className="mx-auto w-full max-w-[900px] text-[var(--cs-navy)]">
        <header className="pb-10 sm:pb-14">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#738093]">
            Over Carmen
          </p>
          <h1 className="mt-4 max-w-[820px] text-[42px] font-semibold leading-[1.04] tracking-[-0.04em] sm:text-[58px]">
            Minder administratie. Meer aandacht voor zorg.
          </h1>
          <p className="mt-8 max-w-[800px] text-[17px] leading-8 text-[#536274]">
            Carmen helpt artsen en zorgteams om gesproken informatie sneller om te zetten in
            bruikbare medische documentatie. De toepassing combineert medische dictatie,
            gestructureerde consultnotities en vertaling, zodat verslaggeving beter meeloopt
            met het werk rond de patiënt.
          </p>
        </header>

        <AboutSection number="01" title="Wat Carmen doet">
          <div className="grid gap-6 sm:grid-cols-3">
            <div>
              <h3 className="font-semibold text-[var(--cs-navy)]">Dictatie</h3>
              <p className="mt-2">
                Spreek bevindingen in en zet ze om in verzorgd medisch Nederlands, klaar om
                verder te gebruiken in uw verslag of brief.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-[var(--cs-navy)]">Sjabloon</h3>
              <p className="mt-2">
                Leg een consult vast en laat Carmen de besproken informatie structureren tot
                een bruikbare medische notitie.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-[var(--cs-navy)]">Vertaling</h3>
              <p className="mt-2">
                Ondersteun gesprekken tussen Nederlands en de taal van de patiënt wanneer een
                taalbarrière de communicatie moeilijker maakt.
              </p>
            </div>
          </div>
        </AboutSection>

        <AboutSection number="02" title="In uw bestaande workflow">
          <p>
            Carmen is gemaakt om te werken naast de toepassingen die zorgverleners vandaag al
            gebruiken. Plaats uw cursor in het EPD, Word, e-mail of een andere applicatie en
            gebruik de afgewerkte tekst waar u die nodig hebt. Zo hoeft u voor documentatie
            niet telkens naar een aparte werkomgeving over te schakelen.
          </p>
          <p>
            De nadruk ligt op medisch Nederlands, herkenbare verslagtaal en zo weinig mogelijk
            correctiewerk na het inspreken.
          </p>
        </AboutSection>

        <AboutSection number="03" title="Voor zorgteams">
          <p>
            Carmen ondersteunt artsen bij consultnotities en brieven, en helpt medewerkers om
            documentatie en overdracht sneller klaar te maken. De medische inhoud blijft onder
            controle van de zorgverlener die het resultaat beoordeelt en gebruikt.
          </p>
          <p>
            Meer informatie over gegevensverwerking en beveiliging vindt u in ons{" "}
            <Link to="/trust" className={TEXT_LINK}>
              Trust Center
            </Link>{" "}
            en het{" "}
            <Link to="/privacy" className={TEXT_LINK}>
              privacybeleid
            </Link>
            .
          </p>
        </AboutSection>

        <AboutSection number="04" title="Kennismaken">
          <p>
            Wilt u weten hoe Carmen in uw praktijk, dienst of bestaande workflow past? Neem
            contact op voor een inhoudelijke kennismaking of hulp bij de onboarding.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center rounded-full bg-[var(--cs-navy)] px-5 py-3 text-[14px] font-semibold text-white transition-colors hover:bg-[var(--cs-navy-strong)]"
          >
            Contact opnemen
          </Link>
        </AboutSection>
      </article>
    </PublicPageLayout>
  );
}
